import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Button, EmptyState, Skeleton, cn } from "@nexus/ui";
import { Pencil } from "lucide-react";
import { adminVendorsQueryOptions, useToggleAdminVendorActive } from "@/lib/queries";
import type { AdminVendorRow } from "@/features/admin/marketplace/types/admin-marketplace.types";
import { getVendorInitials } from "@/features/marketplace/lib/vendorInitials";

const SKELETON_ROWS = 5;

function formatCreatedDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

interface AdminVendorsListProps {
  canList: boolean;
  canEdit: boolean;
  canActivate: boolean;
  canDeactivate: boolean;
  onEdit: (vendor: AdminVendorRow) => void;
}

export function AdminVendorsList({
  canList,
  canEdit,
  canActivate,
  canDeactivate,
  onEdit,
}: AdminVendorsListProps) {
  const { t } = useTranslation();
  const { data: vendors = [], isLoading, isError } = useQuery({
    ...adminVendorsQueryOptions(),
    enabled: canList,
  });
  const toggleActive = useToggleAdminVendorActive();

  if (!canList) {
    return (
      <p className="py-12 text-center text-sm text-muted-foreground">
        {t("common.noPermission", "You don't have permission to view this content.")}
      </p>
    );
  }

  if (isLoading) {
    return <AdminVendorsListSkeleton />;
  }

  if (isError) {
    return (
      <div className="py-12 text-center">
        <p className="font-medium">{t("marketplace.admin.errorTitle")}</p>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("errors.tryAgain", "Please try again.")}
        </p>
      </div>
    );
  }

  if (vendors.length === 0) {
    return (
      <EmptyState
        title={t("marketplace.admin.empty")}
        description={t("marketplace.admin.emptyDescription", "Add your first vendor to make it available in the catalog.")}
      />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="px-0.5 text-xs font-medium tabular-nums text-muted-foreground">
        {t("marketplace.admin.vendorCount", {
          count: vendors.length,
          defaultValue: "{{count}} vendor",
          defaultValue_other: "{{count}} vendors",
        })}
      </p>
      <div className="overflow-hidden rounded-xl border border-border/60 bg-card">
        <table className="w-full text-sm">
          <thead className="bg-muted/40">
            <tr className="border-b border-border/50">
              <th className="px-5 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground/70">
                {t("marketplace.admin.table.name")}
              </th>
              <th className="hidden px-5 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground/70 md:table-cell">
                {t("marketplace.admin.table.contactEmail")}
              </th>
              <th className="hidden px-5 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground/70 sm:table-cell">
                {t("marketplace.admin.table.catalog", { defaultValue: "Catalog" })}
              </th>
              <th className="hidden px-5 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground/70 lg:table-cell">
                {t("marketplace.admin.table.created", { defaultValue: "Created" })}
              </th>
              <th className="px-5 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground/70">
                {t("marketplace.admin.table.status")}
              </th>
              <th className="px-5 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border/50">
            {vendors.map((vendor) => (
              <VendorRow
                key={vendor.id}
                vendor={vendor}
                onEdit={() => onEdit(vendor)}
                onToggleActive={() =>
                  toggleActive.mutate({ vendorId: vendor.id, activate: !vendor.isActive })
                }
                isToggling={
                  toggleActive.isPending && toggleActive.variables?.vendorId === vendor.id
                }
                anyToggling={toggleActive.isPending}
                canEdit={canEdit}
                canActivate={canActivate}
                canDeactivate={canDeactivate}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AdminVendorsListSkeleton() {
  return (
    <div className="overflow-hidden rounded-xl border border-border/60 bg-card">
      <table className="w-full text-sm">
        <tbody className="divide-y divide-border/50">
          {Array.from({ length: SKELETON_ROWS }).map((_, i) => (
            <tr key={i}>
              <td className="px-5 py-3.5">
                <div className="flex items-center gap-3">
                  <Skeleton className="h-10 w-10 rounded-lg" />
                  <div className="flex flex-col gap-1.5">
                    <Skeleton className="h-3.5 w-32" />
                    <Skeleton className="h-3 w-20" />
                  </div>
                </div>
              </td>
              <td className="hidden px-5 py-3.5 md:table-cell">
                <Skeleton className="h-3 w-40" />
              </td>
              <td className="hidden px-5 py-3.5 sm:table-cell">
                <Skeleton className="h-3 w-24" />
              </td>
              <td className="hidden px-5 py-3.5 lg:table-cell">
                <Skeleton className="h-3 w-20" />
              </td>
              <td className="px-5 py-3.5">
                <Skeleton className="h-3 w-16" />
              </td>
              <td className="px-5 py-3.5">
                <Skeleton className="ml-auto h-8 w-24" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface VendorRowProps {
  vendor: AdminVendorRow;
  onEdit: () => void;
  onToggleActive: () => void;
  isToggling: boolean;
  anyToggling: boolean;
  canEdit: boolean;
  canActivate: boolean;
  canDeactivate: boolean;
}

function VendorRow({
  vendor,
  onEdit,
  onToggleActive,
  isToggling,
  anyToggling,
  canEdit,
  canActivate,
  canDeactivate,
}: VendorRowProps) {
  const { t } = useTranslation();
  const canToggle = vendor.isActive ? canDeactivate : canActivate;

  return (
    <tr className="group transition-colors duration-150 ease-out hover:bg-muted/40">
      <td className="px-5 py-3.5">
        <div className="flex items-center gap-3">
          {vendor.logoUrl ? (
            <img
              src={vendor.logoUrl}
              alt=""
              className="h-10 w-10 shrink-0 rounded-lg border border-border/60 bg-background object-contain"
              aria-hidden
            />
          ) : (
            <div
              aria-hidden
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-xs font-semibold text-primary"
            >
              {getVendorInitials(vendor.name)}
            </div>
          )}
          <div className="flex min-w-0 flex-col">
            <span className="truncate font-medium tracking-tight text-foreground">
              {vendor.name}
            </span>
            <span className="truncate font-mono text-xs text-muted-foreground">
              {vendor.slug}
            </span>
          </div>
        </div>
      </td>
      <td className="hidden px-5 py-3.5 text-xs text-muted-foreground md:table-cell">
        {vendor.contactEmail ? (
          <div className="flex flex-col gap-0.5">
            <span className="text-foreground/80">{vendor.contactEmail}</span>
            {vendor.pendingContactEmail && (
              <span className="text-status-warning">
                {t("marketplace.admin.pendingEmail", {
                  email: vendor.pendingContactEmail,
                })}
              </span>
            )}
          </div>
        ) : (
          <span className="text-muted-foreground/50">—</span>
        )}
      </td>
      <td className="hidden px-5 py-3.5 text-xs text-muted-foreground sm:table-cell">
        <span className="tabular-nums">
          {t("marketplace.admin.table.packagesCount", {
            count: vendor.packages.length,
            defaultValue: "{{count}} pkg",
            defaultValue_other: "{{count}} pkgs",
          })}
          <span className="px-1.5 text-muted-foreground/40">·</span>
          {t("marketplace.admin.table.questionsCount", {
            count: vendor.questions.length,
            defaultValue: "{{count}} question",
            defaultValue_other: "{{count}} questions",
          })}
        </span>
      </td>
      <td className="hidden px-5 py-3.5 text-xs text-muted-foreground lg:table-cell">
        <time dateTime={vendor.createdAt} className="tabular-nums whitespace-nowrap">
          {formatCreatedDate(vendor.createdAt)}
        </time>
      </td>
      <td className="px-5 py-3.5">
        <span
          className={cn(
            "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium",
            vendor.isActive
              ? "bg-status-success-subtle text-status-success"
              : "bg-muted text-muted-foreground",
          )}
        >
          <span
            aria-hidden
            className={cn(
              "h-1.5 w-1.5 rounded-full",
              vendor.isActive ? "bg-status-success" : "bg-muted-foreground/40",
            )}
          />
          {vendor.isActive
            ? t("marketplace.admin.status.active")
            : t("marketplace.admin.status.inactive")}
        </span>
      </td>
      <td className="px-5 py-3.5">
        <div className="flex items-center justify-end gap-1.5">
          {canEdit && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground hover:text-foreground"
              onClick={onEdit}
              title={t("marketplace.admin.editVendor")}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          )}
          {canToggle && (
            <Button
              variant="outline"
              size="sm"
              disabled={anyToggling}
              onClick={onToggleActive}
            >
              {isToggling
                ? vendor.isActive
                  ? t("marketplace.admin.deactivating")
                  : t("marketplace.admin.activating")
                : vendor.isActive
                  ? t("marketplace.admin.deactivate")
                  : t("marketplace.admin.activate")}
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}
